import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { t as tFn } from "./translations";
import { readPref, writePref } from "../utils/prefs";

const PREF_KEY = "idb_lang";
const LEGACY_LS_KEY = "alterego_lang";
const LanguageContext = createContext({ lang: "it", setLang: () => {}, t: (k) => k, tEn: (k) => k });

function readInitialLang() {
  // Cookie first
  const fromCookie = readPref(PREF_KEY, "");
  if (fromCookie === "it" || fromCookie === "en") return fromCookie;
  // One-time migration from legacy localStorage value (if present)
  try {
    const legacy = localStorage.getItem(LEGACY_LS_KEY);
    if (legacy === "it" || legacy === "en") {
      writePref(PREF_KEY, legacy);
      try {
        localStorage.removeItem(LEGACY_LS_KEY);
      } catch (err) {
        // Private mode / storage quota — migration still logically succeeded
        // because we already copied the value into the cookie above.
        console.warn("[LanguageContext] removeItem legacy key failed:", err);
      }
      return legacy;
    }
  } catch (err) {
    // localStorage unavailable (private mode, cookie-blocked). Ignore and fall back.
    console.warn("[LanguageContext] legacy localStorage read failed:", err);
  }
  return "it";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((code) => {
    setLangState(code);
    writePref(PREF_KEY, code);
    try {
      document.documentElement.setAttribute("lang", code);
    } catch (err) {
      // Non-DOM environments (SSR, jest) — safe to ignore.
      console.warn("[LanguageContext] set <html lang> failed:", err);
    }
  }, []);

  const t = useCallback((key) => tFn(key, lang), [lang]);
  // tEn always returns the English translation, regardless of selected language.
  // Used for UI chrome that must stay in English (e.g. sidebar menu).
  const tEn = useCallback((key) => tFn(key, "en"), []);

  const value = useMemo(() => ({ lang, setLang, t, tEn }), [lang, setLang, t, tEn]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
